import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { useFormikContext } from 'formik';
import { getTrans } from 'modules/i18n';
import { CVBuilderFormValues } from 'utils/cv-form-db';
import {
  SelectedCount,
  SuggestionChips
} from '../../components/SuggestionChips';
import { getSoftSkillOptions } from './constants';

interface SoftSkillsFormProps {
  handleSkillChange: (newSkills: string[]) => void;
  handleRemoveSkill: (skillToRemove: string) => void;
}

/**
 * Soft-skill picker: type-to-search with the curated list ALSO shown as tappable chips.
 *
 * The curated options existed before but were reachable only by opening the autocomplete's
 * dropdown, and a separate "additional skill" text field with its own Add button sat
 * underneath — duplicating what the `freeSolo` input already does (type a value, press
 * Enter). That second field is gone; the suggestions moved onto the screen.
 */
export const SoftSkillsForm = ({
  handleSkillChange,
  handleRemoveSkill
}: SoftSkillsFormProps) => {
  const { values } = useFormikContext<CVBuilderFormValues>();
  const selected = values.softSkills || [];

  const handleAddSuggestion = (option: string) => {
    if (!selected.includes(option)) {
      handleSkillChange([...selected, option]);
    }
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {getTrans('cvBuilderPage.softSkills.instruction')}
      </Box>

      <Box sx={{ mb: 1 }}>
        <Autocomplete
          multiple
          freeSolo
          options={getSoftSkillOptions(values.language)}
          value={selected}
          onChange={(_, newValue) => handleSkillChange(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={`${option}-${index}`}
                onDelete={() => handleRemoveSkill(option)}
                sx={theme => ({
                  backgroundColor: theme.palette.primary.lighter,
                  color: theme.palette.primary.dark,
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.primary.main
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.lighter,
                    opacity: 0.8
                  }
                })}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              label={getTrans('cvBuilderPage.softSkills.form.softSkills.label')}
              placeholder={getTrans(
                'cvBuilderPage.softSkills.form.softSkills.placeholder'
              )}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <SelectedCount count={selected.length} />
      </Box>

      <SuggestionChips
        label={getTrans('cvBuilderPage.softSkills.suggestions.label')}
        options={getSoftSkillOptions(values.language)}
        selected={selected}
        onAdd={handleAddSuggestion}
      />
    </>
  );
};
